/**
 * Centralized conversation logging service
 * Provides unified interface for logging conversations with proper error handling,
 * validation, and retry logic for both completed and interrupted conversations.
 */

import EnvironmentDetector from '../utils/EnvironmentDetector.js';
import ErrorLogger from '../utils/ErrorLogger.js';

class ConversationLoggerService {
  constructor() {
    this.logEndpoint = '/.netlify/functions/logConversation';
    this.maxRetries = 3;
    this.baseRetryDelay = 1000; // Base delay: 1 second
    this.maxRetryDelay = 10000; // Maximum delay: 10 seconds
  }

  /**
   * Validates conversation log data before sending
   * @param {Object} logData - The conversation log data to validate
   * @returns {Object} - Validation result with isValid flag and errors array
   */
  validateLogData(logData) {
    const errors = [];
    const conversationLog = logData.conversation_log;

    if (!conversationLog) {
      errors.push('Missing conversation_log object');
      return { isValid: false, errors };
    }

    // Required fields validation
    const requiredFields = ['id', 'model_used', 'status', 'message_count', 'started_at', 'ended_at', 'environment'];
    requiredFields.forEach(field => {
      if (conversationLog[field] === undefined || conversationLog[field] === null) {
        errors.push(`Missing required field: ${field}`);
      }
    });

    // Status validation
    const validStatuses = ['completed', 'interrupted', 'error'];
    const validReasoningEfforts = ['minimal', 'low', 'medium', 'high', 'xhigh'];
    if (conversationLog.status && !validStatuses.includes(conversationLog.status)) {
      errors.push(`Invalid status: ${conversationLog.status}. Must be one of: ${validStatuses.join(', ')}`);
    }

    if (conversationLog.reasoning_effort && !validReasoningEfforts.includes(conversationLog.reasoning_effort)) {
      errors.push(`Invalid reasoning_effort: ${conversationLog.reasoning_effort}. Must be one of: ${validReasoningEfforts.join(', ')}`);
    }

    // Environment validation
    const validEnvironments = ['development', 'staging', 'production', 'unknown'];
    if (conversationLog.environment && !validEnvironments.includes(conversationLog.environment)) {
      errors.push(`Invalid environment: ${conversationLog.environment}. Must be one of: ${validEnvironments.join(', ')}`);
    }

    // Numeric field validation
    const numericFields = [
      'message_count',
      'new_input_tokens',
      'cached_tokens',
      'output_tokens',
      'new_input_text_tokens',
      'new_input_audio_tokens',
      'cached_text_tokens',
      'cached_audio_tokens',
      'output_text_tokens',
      'output_audio_tokens',
      'total_cost',
      // Snapshot fields (optional)
      'max_input_tokens',
      'max_output_tokens',
    ];
    // warning_threshold is numeric 0..1
    numericFields.forEach(field => {
      if (conversationLog[field] !== undefined && conversationLog[field] !== null) {
        if (typeof conversationLog[field] !== 'number' || conversationLog[field] < 0) {
          errors.push(`Invalid ${field}: must be a non-negative number`);
        }
      }
    });

    if (conversationLog.usage_events !== undefined && !Array.isArray(conversationLog.usage_events)) {
      errors.push('Invalid usage_events: must be an array');
    }

    if (conversationLog.warning_threshold !== undefined) {
      const wt = Number(conversationLog.warning_threshold);
      if (Number.isNaN(wt) || wt < 0 || wt > 1.0) {
        errors.push('Invalid warning_threshold: must be a number between 0 and 1');
      }
    }

    // Date validation
    const dateFields = ['started_at', 'ended_at'];
    dateFields.forEach(field => {
      if (conversationLog[field]) {
        const date = new Date(conversationLog[field]);
        if (isNaN(date.getTime())) {
          errors.push(`Invalid ${field}: must be a valid ISO date string`);
        }
      }
    });

    return { isValid: errors.length === 0, errors };
  }

  /**
   * Sanitizes and normalizes conversation log data
   * @param {Object} logData - Raw conversation log data
   * @returns {Object} - Sanitized conversation log data
   */
  sanitizeLogData(logData) {
    const conversationLog = logData.conversation_log || {};
    
    // Detect environment for this log entry
    const environment = this.detectAndValidateEnvironment();
    
    return {
      conversation_log: {
        id: conversationLog.id || this.generateConversationId(),
        user_id: conversationLog.user_id || null,
        quest_pack: conversationLog.quest_pack || null,
        model_used: conversationLog.model_used || "gpt-realtime-1.5",
        reasoning_effort: conversationLog.reasoning_effort || "low",
        status: conversationLog.status || "error",
        message_count: Math.max(0, parseInt(conversationLog.message_count) || 0),
        started_at: conversationLog.started_at || new Date().toISOString(),
        ended_at: conversationLog.ended_at || new Date().toISOString(),
        new_input_tokens: Math.max(0, parseInt(conversationLog.new_input_tokens) || 0),
        cached_tokens: Math.max(0, parseInt(conversationLog.cached_tokens) || 0),
        output_tokens: Math.max(0, parseInt(conversationLog.output_tokens) || 0),
        new_input_text_tokens: Math.max(0, parseInt(conversationLog.new_input_text_tokens) || 0),
        new_input_audio_tokens: Math.max(0, parseInt(conversationLog.new_input_audio_tokens) || 0),
        cached_text_tokens: Math.max(0, parseInt(conversationLog.cached_text_tokens) || 0),
        cached_audio_tokens: Math.max(0, parseInt(conversationLog.cached_audio_tokens) || 0),
        output_text_tokens: Math.max(0, parseInt(conversationLog.output_text_tokens) || 0),
        output_audio_tokens: Math.max(0, parseInt(conversationLog.output_audio_tokens) || 0),
        total_cost: Math.max(0, parseFloat(conversationLog.total_cost) || 0),
        usage_events: Array.isArray(conversationLog.usage_events) ? conversationLog.usage_events : [],
        client_info: conversationLog.client_info || {},
        error_details: conversationLog.error_details !== undefined && conversationLog.error_details !== null ? conversationLog.error_details : null,
        environment: environment,
        // Snapshot fields: pass through if present
        max_input_tokens: conversationLog.max_input_tokens !== undefined && conversationLog.max_input_tokens !== null
          ? Math.max(0, parseInt(conversationLog.max_input_tokens))
          : undefined,
        max_output_tokens: conversationLog.max_output_tokens !== undefined && conversationLog.max_output_tokens !== null
          ? Math.max(0, parseInt(conversationLog.max_output_tokens))
          : undefined,
        warning_threshold: conversationLog.warning_threshold !== undefined && conversationLog.warning_threshold !== null
          ? Math.max(0, Math.min(1, parseFloat(conversationLog.warning_threshold)))
          : undefined,
      }
    };
  }

  /**
   * Detects and validates the current environment
   * @returns {string} - Validated environment string
   */
  detectAndValidateEnvironment() {
    try {
      const environment = EnvironmentDetector.detectEnvironment();
      
      // Validate that environment is one of the expected values
      const validEnvironments = ['development', 'staging', 'production', 'unknown'];
      if (!validEnvironments.includes(environment)) {
        ErrorLogger.logError(
          'Environment Detection',
          `Invalid environment detected: ${environment}`,
          { detectedEnvironment: environment, validEnvironments },
          'warn'
        );
        return 'unknown';
      }
      
      return environment;
    } catch (error) {
      ErrorLogger.logError(
        'Environment Detection',
        error,
        { operation: 'detectAndValidateEnvironment' }
      );
      return 'unknown';
    }
  }

  /**
   * Calculates exponential backoff delay for retry attempts
   * @param {number} attempt - Current attempt number (1-based)
   * @returns {number} - Delay in milliseconds
   */
  calculateRetryDelay(attempt) {
    // Exponential backoff: 1s, 2s, 4s, capped at maxRetryDelay
    const delay = this.baseRetryDelay * Math.pow(2, attempt - 1);
    return Math.min(delay, this.maxRetryDelay);
  }

  getAuthToken() {
    try {
      if (typeof localStorage === 'undefined') {
        return null;
      }

      return localStorage.getItem('auth_token') || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Performs HTTP request with enhanced retry logic and exponential backoff
   * @param {Object} payload - The payload to send
   * @param {number} attempt - Current attempt number (for retry logic)
   * @returns {Promise<Response>} - The fetch response
   */
  async makeRequest(payload, attempt = 1) {
    const environment = this.detectAndValidateEnvironment();
    const token = this.getAuthToken();
    const requestPayload = token
      ? { ...payload, token }
      : payload;
    
    try {
      const response = await fetch(this.logEndpoint, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestPayload),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unable to read response');
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      // Log successful request after retries
      if (attempt > 1) {
        console.log(`Conversation logging succeeded on attempt ${attempt} in ${environment} environment`);
      }

      return response;
    } catch (error) {
      const isLastAttempt = attempt >= this.maxRetries;
      
      // Enhanced error logging with environment context
      const errorContext = {
        attempt,
        maxRetries: this.maxRetries,
        environment,
        endpoint: this.logEndpoint,
        payloadSize: JSON.stringify(requestPayload).length,
        requestHeaders: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      };

      if (isLastAttempt) {
        ErrorLogger.logError(
          'HTTP Request - Final Failure',
          error,
          errorContext
        );
        throw error;
      } else {
        const retryDelay = this.calculateRetryDelay(attempt);
        ErrorLogger.logRetryAttempt(
          'HTTP Request - Retry',
          attempt,
          this.maxRetries,
          error,
          retryDelay
        );
        
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return this.makeRequest(payload, attempt + 1);
      }
    }
  }

  /**
   * Logs a completed conversation
   * @param {Object} conversationData - Basic conversation metadata
   * @param {Object} usageData - Token usage and cost data
   * @param {Object} metadata - Additional metadata (client info, etc.)
   * @returns {Promise<boolean>} - Success status
   */
  async logConversation(conversationData, usageData = {}, metadata = {}) {
    try {
      const payload = this.sanitizeLogData({
        conversation_log: {
          ...conversationData,
          ...usageData,
          ...metadata,
          status: 'completed'
        }
      });

      const validation = this.validateLogData(payload);
      if (!validation.isValid) {
        ErrorLogger.logValidationErrors(
          'Conversation Log Validation',
          validation.errors,
          payload
        );
        // Continue with sanitized data rather than failing completely
      }

      await this.makeRequest(payload);
      ErrorLogger.logError(
        'Conversation Logging',
        'Conversation logged successfully',
        { 
          operation: 'logConversation',
          status: 'completed',
          questPack: conversationData.quest_pack,
          messageCount: conversationData.message_count
        },
        'info'
      );
      return true;
    } catch (error) {
      ErrorLogger.logError(
        'Conversation Logging - Final Failure',
        error,
        {
          operation: 'logConversation',
          conversationData: ErrorLogger.sanitizeMetadata(conversationData),
          usageData,
          metadata: ErrorLogger.sanitizeMetadata(metadata)
        }
      );
      return false;
    }
  }

  /**
   * Logs an interrupted conversation (e.g., page unload, connection loss)
   * @param {Object} conversationData - Basic conversation metadata
   * @param {Object} usageData - Token usage and cost data
   * @param {Object} metadata - Additional metadata (client info, etc.)
   * @returns {Promise<boolean>} - Success status
   */
  async logInterruption(conversationData, usageData = {}, metadata = {}) {
    try {
      const payload = this.sanitizeLogData({
        conversation_log: {
          ...conversationData,
          ...usageData,
          ...metadata,
          status: 'interrupted',
          ended_at: new Date().toISOString()
        }
      });

      const validation = this.validateLogData(payload);
      if (!validation.isValid) {
        ErrorLogger.logValidationErrors(
          'Interruption Log Validation',
          validation.errors,
          payload
        );
        // Continue with sanitized data rather than failing completely
      }

      // For interruptions, use sendBeacon if available for better reliability during page unload
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const success = navigator.sendBeacon(
          this.logEndpoint, 
          JSON.stringify({
            ...payload,
            ...(this.getAuthToken() ? { token: this.getAuthToken() } : {}),
          })
        );
        if (success) {
          ErrorLogger.logError(
            'Interruption Logging',
            'Interruption logged via sendBeacon',
            { 
              operation: 'logInterruption',
              method: 'sendBeacon',
              status: 'interrupted'
            },
            'info'
          );
          return true;
        }
      }

      // Fallback to regular fetch
      await this.makeRequest(payload);
      ErrorLogger.logError(
        'Interruption Logging',
        'Interruption logged successfully via fetch',
        { 
          operation: 'logInterruption',
          method: 'fetch',
          status: 'interrupted'
        },
        'info'
      );
      return true;
    } catch (error) {
      ErrorLogger.logError(
        'Interruption Logging - Final Failure',
        error,
        {
          operation: 'logInterruption',
          conversationData: ErrorLogger.sanitizeMetadata(conversationData),
          usageData,
          metadata: ErrorLogger.sanitizeMetadata(metadata)
        }
      );
      return false;
    }
  }

  /**
   * Logs a conversation that ended due to an error
   * @param {Object} conversationData - Basic conversation metadata
   * @param {Object} usageData - Token usage and cost data
   * @param {Object} metadata - Additional metadata (client info, etc.)
   * @param {string} errorDetails - Details about the error that occurred
   * @returns {Promise<boolean>} - Success status
   */
  async logError(conversationData, usageData = {}, metadata = {}, errorDetails = null) {
    try {
      const payload = this.sanitizeLogData({
        conversation_log: {
          ...conversationData,
          ...usageData,
          ...metadata,
          status: 'error',
          error_details: errorDetails,
          ended_at: new Date().toISOString()
        }
      });

      const validation = this.validateLogData(payload);
      if (!validation.isValid) {
        ErrorLogger.logValidationErrors(
          'Error Log Validation',
          validation.errors,
          payload
        );
        // Continue with sanitized data rather than failing completely
      }

      await this.makeRequest(payload);
      ErrorLogger.logError(
        'Error Conversation Logging',
        'Error conversation logged successfully',
        { 
          operation: 'logError',
          status: 'error',
          errorDetails: errorDetails
        },
        'info'
      );
      return true;
    } catch (error) {
      ErrorLogger.logError(
        'Error Conversation Logging - Final Failure',
        error,
        {
          operation: 'logError',
          conversationData: ErrorLogger.sanitizeMetadata(conversationData),
          usageData,
          metadata: ErrorLogger.sanitizeMetadata(metadata),
          originalErrorDetails: errorDetails
        }
      );
      return false;
    }
  }

  /**
   * Generates a unique conversation ID (UUID v4 format)
   * @returns {string} - Unique conversation ID in UUID format
   */
  generateConversationId() {
    // Generate a UUID v4 compatible string
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Creates a standardized conversation data object
   * @param {Object} params - Parameters for creating conversation data
   * @returns {Object} - Standardized conversation data object
   */
  createConversationData({
    id = null,
    userId = null,
    questPack = null,
    modelUsed = "gpt-realtime-1.5",
    reasoningEffort = "low",
    messageCount = 0,
    startedAt = null,
    endedAt = null,
    maxInputTokens = undefined,
    maxOutputTokens = undefined,
    warningThreshold = undefined,
  } = {}) {
    return {
      id: id || this.generateConversationId(),
      user_id: userId,
      quest_pack: questPack,
      model_used: modelUsed,
      reasoning_effort: reasoningEffort,
      message_count: messageCount,
      started_at: startedAt || new Date().toISOString(),
      ended_at: endedAt || new Date().toISOString(),
      max_input_tokens: maxInputTokens,
      max_output_tokens: maxOutputTokens,
      warning_threshold: warningThreshold,
    };
  }

  /**
   * Creates a standardized usage data object
   * @param {Object} params - Parameters for creating usage data
   * @returns {Object} - Standardized usage data object
   */
  createUsageData({
    newInputTokens = 0,
    cachedTokens = 0,
    outputTokens = 0,
    totalCost = 0,
    newInputTextTokens = 0,
    newInputAudioTokens = 0,
    cachedTextTokens = 0,
    cachedAudioTokens = 0,
    outputTextTokens = 0,
    outputAudioTokens = 0,
    usageEvents = []
  } = {}) {
    return {
      new_input_tokens: newInputTokens,
      cached_tokens: cachedTokens,
      output_tokens: outputTokens,
      new_input_text_tokens: newInputTextTokens,
      new_input_audio_tokens: newInputAudioTokens,
      cached_text_tokens: cachedTextTokens,
      cached_audio_tokens: cachedAudioTokens,
      output_text_tokens: outputTextTokens,
      output_audio_tokens: outputAudioTokens,
      total_cost: totalCost,
      usage_events: usageEvents
    };
  }

  /**
   * Creates a standardized metadata object
   * @param {Object} params - Parameters for creating metadata
   * @returns {Object} - Standardized metadata object
   */
  createMetadata({
    clientInfo = {},
    errorDetails = null
  } = {}) {
    return {
      client_info: clientInfo,
      error_details: errorDetails
    };
  }
}

// Create and export singleton instance
export const conversationLogger = new ConversationLoggerService();

// Export the class for testing purposes
export { ConversationLoggerService };

export default conversationLogger;
